import { useState, useCallback } from 'react';
import { Shuffle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const RandomPicker = () => {
  useToolUsage('random-picker');
  const [items, setItems] = useState('Apple\nBanana\nCherry\nDate\nElderberry');
  const [count, setCount] = useState(1);
  const [results, setResults] = useState<string[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const pick = useCallback(() => {
    const list = items.split('\n').map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error('Add some items first'); return; }
    const n = Math.min(count, list.length);
    setIsAnimating(true);

    // Animate through random picks
    let frame = 0;
    const interval = setInterval(() => {
      const shuffled = [...list].sort(() => Math.random() - 0.5);
      setResults(shuffled.slice(0, n));
      frame++;
      if (frame >= 15) {
        clearInterval(interval);
        const final = [...list].sort(() => Math.random() - 0.5).slice(0, n);
        setResults(final);
        setIsAnimating(false);
        toast.success(`Picked ${n} item${n > 1 ? 's' : ''}!`);
      }
    }, 80);
  }, [items, count]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Shuffle className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Random Picker</h1>
          <p className="text-muted-foreground">Randomly pick items from a list</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Items (one per line)</Label>
              <Textarea value={items} onChange={e => setItems(e.target.value)} className="min-h-[200px] font-mono" placeholder="Apple&#10;Banana&#10;Cherry" />
            </div>
            <div>
              <Label className="text-sm mb-2 block">Pick count</Label>
              <Input type="number" value={count} onChange={e => setCount(Math.max(1, Number(e.target.value)))} min={1} />
            </div>
            <Button onClick={pick} disabled={isAnimating} className="w-full">
              <Shuffle className="h-4 w-4 mr-2" /> {isAnimating ? 'Picking...' : 'Pick Random'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            {results.length > 0 ? (
              <div className="space-y-3">
                <Label className="text-sm text-muted-foreground">Result{results.length > 1 ? 's' : ''}:</Label>
                {results.map((r, i) => (
                  <div key={i} className={`p-4 rounded-xl bg-primary/10 border border-primary/20 text-center transition-all duration-300 ${isAnimating ? 'scale-95 opacity-70' : 'scale-100'}`}>
                    <p className="text-xl font-bold text-primary">{r}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                <p className="text-sm">Click "Pick Random" to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RandomPicker;
